import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';

export interface CheckboxProps extends Omit<
  ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
  'children'
> {
  label?: ReactNode;
  description?: ReactNode;
}

/** Casilla de 20 px dentro de una fila táctil de 44 px; con etiqueta, toda la fila alterna el valor. */
export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(function Checkbox(
  { label, description, className, id, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const descId = description ? `${inputId}-desc` : undefined;
  return (
    <div className={cn('flex min-h-11 items-start gap-3 py-2', className)}>
      <CheckboxPrimitive.Root
        ref={ref}
        id={inputId}
        aria-describedby={descId}
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-sm border border-input bg-card text-primary-fg',
          'transition-colors duration-fast ease-standard',
          'data-[state=checked]:border-primary data-[state=indeterminate]:border-primary data-[state=checked]:bg-primary data-[state=indeterminate]:bg-primary',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...rest}
      >
        <CheckboxPrimitive.Indicator>
          <Icon icon={rest.checked === 'indeterminate' ? Minus : Check} size={16} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label ? (
        <div className="min-w-0">
          <label htmlFor={inputId} className="block cursor-pointer text-base text-fg">
            {label}
          </label>
          {description ? (
            <p id={descId} className="text-sm text-fg-muted">
              {description}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
});
